import { Injectable } from '@angular/core';

@Injectable({
  providedIn: 'root'
})
export class ExportService {

  /**
   * Export data to Excel format
   */
  exportToExcel(data: any[], filename: string = 'export'): void {
    if (!data || data.length === 0) {
      console.warn('No data to export');
      return;
    }

    // Convert data to CSV format first (Excel can read CSV)
    const csvContent = this.convertToCSV(data);
    this.downloadFile(csvContent, `${filename}.csv`, 'text/csv;charset=utf-8;');
  }

  /**
   * Export data to PDF format
   */
  exportToPDF(data: any[], filename: string = 'export', title: string = 'Export'): void {
    if (!data || data.length === 0) {
      console.warn('No data to export');
      return;
    }

    // Create a simple HTML table and print as PDF
    const htmlContent = this.convertToHTMLTable(data, title);
    const printWindow = window.open('', '_blank');
    
    if (printWindow) {
      printWindow.document.write(htmlContent);
      printWindow.document.close();
      printWindow.focus();
      
      setTimeout(() => {
        printWindow.print();
        printWindow.close();
      }, 250);
    }
  }

  /**
   * Export data to CSV format
   */
  exportToCSV(data: any[], filename: string = 'export'): void {
    if (!data || data.length === 0) {
      console.warn('No data to export');
      return;
    }

    const csvContent = this.convertToCSV(data);
    this.downloadFile(csvContent, `${filename}.csv`, 'text/csv;charset=utf-8;');
  }

  /**
   * Convert JSON data to CSV format
   */
  private convertToCSV(data: any[]): string {
    if (data.length === 0) return '';

    // Get headers from first object
    const headers = Object.keys(data[0]);
    
    // Create CSV header row
    let csv = headers.join(',') + '\n';

    // Add data rows
    data.forEach(row => {
      const values = headers.map(header => {
        const value = row[header];
        // Handle values that contain commas or quotes
        if (value === null || value === undefined) return '';
        let stringValue = String(value);
        // Neutralize CSV formula injection (=, +, -, @ prefixes execute as formulas in Excel/Sheets)
        if (/^[=+\-@\t\r]/.test(stringValue)) {
          stringValue = `'${stringValue}`;
        }
        if (stringValue.includes(',') || stringValue.includes('"') || stringValue.includes('\n')) {
          return `"${stringValue.replace(/"/g, '""')}"`;
        }
        return stringValue;
      });
      csv += values.join(',') + '\n';
    });

    return csv;
  }

  private escapeHtml(value: unknown): string {
    const str = value === null || value === undefined ? '' : String(value);
    return str
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#x27;');
  }

  /**
   * Convert JSON data to HTML table for PDF export
   */
  private convertToHTMLTable(data: any[], title: string): string {
    if (data.length === 0) return '';

    const headers = Object.keys(data[0]);
    const safeTitle = this.escapeHtml(title);

    let html = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>${safeTitle}</title>
        <style>
          @media print {
            @page { size: landscape; }
          }
          body {
            font-family: Arial, sans-serif;
            margin: 20px;
          }
          h1 {
            color: #1B87C7;
            margin-bottom: 20px;
          }
          table {
            width: 100%;
            border-collapse: collapse;
            margin-top: 20px;
          }
          th, td {
            border: 1px solid #ddd;
            padding: 12px;
            text-align: left;
          }
          th {
            background-color: #1B87C7;
            color: white;
            font-weight: bold;
          }
          tr:nth-child(even) {
            background-color: #f9f9f9;
          }
          tr:hover {
            background-color: #f1f1f1;
          }
        </style>
      </head>
      <body>
        <h1>${safeTitle}</h1>
        <p>Generated on: ${new Date().toLocaleDateString()} ${new Date().toLocaleTimeString()}</p>
        <table>
          <thead>
            <tr>
              ${headers.map(header => `<th>${this.escapeHtml(this.formatHeader(header))}</th>`).join('')}
            </tr>
          </thead>
          <tbody>
            ${data.map(row => `
              <tr>
                ${headers.map(header => `<td>${this.escapeHtml(row[header])}</td>`).join('')}
              </tr>
            `).join('')}
          </tbody>
        </table>
      </body>
      </html>
    `;

    return html;
  }

  /**
   * Format header names (convert camelCase to Title Case)
   */
  private formatHeader(header: string): string {
    return header
      .replace(/([A-Z])/g, ' $1')
      .replace(/^./, str => str.toUpperCase())
      .trim();
  }

  /**
   * Download file to user's computer
   */
  private downloadFile(content: string, filename: string, mimeType: string): void {
    const blob = new Blob([content], { type: mimeType });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.click();
    window.URL.revokeObjectURL(url);
  }
}

